/** Auxiliary info — the wizard host for AUX_INFO_SYNC.
 *
 *  Launches the sync_aux_info task THROUGH the preflight item (stage +
 *  execute, same as the other data steps) so the item owns the task and the
 *  poller completes it. The task pauses in AWAITING_APPROVAL — the review
 *  happens on the full-page grid at /sync/aux-info/:taskId/review (the
 *  wizard shows a prominent deep link instead of embedding thousands of
 *  rows into the rail layout). The item stays STARTED through the pause and
 *  completes once the approved selection is materialized.
 */
import HistoryIcon from '@mui/icons-material/History';
import RateReviewIcon from '@mui/icons-material/RateReview';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  IconButton,
  ListItemText,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFnsV3';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import type { PreflightItem } from '@/api/preflight';
import { useSnackbar } from '@/components/Snackbar';
import { BlockedNotice } from '@/features/preflight/PreflightBlockers';
import { TaskProgressCard } from '@/features/bgtasks/TaskProgressCard';

import { PreflightItemRunLogDrawer } from '../../tabs/PreflightRunLog';
import type { PreflightItemActions } from '../../tabs/usePreflightItemActions';
import { taskIdOf } from '../SetupItemPanel';
import { useItemLaunch, type IntegrationOption } from './DataSyncStep';

interface AuxDetail {
  integration_options?: IntegrationOption[];
  sync_params?: Record<string, unknown>;
  bgtask?: { id?: string; state_display?: string } | null;
}

interface Props {
  item: PreflightItem;
  topId: number;
  canManage: boolean;
  actions: PreflightItemActions;
}

export function AuxInfoStep({ item, topId, canManage, actions }: Props) {
  const snackbar = useSnackbar();
  const detail = (item.detail ?? {}) as AuxDetail;
  const options = detail.integration_options ?? [];
  const launch = useItemLaunch(topId, item, actions);

  const [integration, setIntegration] = useState<string | null>(null);
  /** Account slugs to sample; null = every configured account. */
  const [accountSlugs, setAccountSlugs] = useState<(string | null)[] | null>(null);
  // The project's date pair is a pair of DatePickers over `Date` (see the sync
  // forms one step away); the wire format is the `yyyy-MM-dd` the backend takes.
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [logOpen, setLogOpen] = useState(false);
  const [launching, setLaunching] = useState(false);
  const taskId = taskIdOf(item);
  const running = item.status === 'started';

  useEffect(() => {
    if (integration || options.length === 0) return;
    const staged = detail.sync_params ?? {};
    const stagedName = typeof staged.integration_name === 'string' ? staged.integration_name : null;
    setIntegration(
      options.find((o) => o.integration === stagedName)?.integration ?? options[0]!.integration,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.length]);

  const accounts = options.find((o) => o.integration === integration)?.accounts ?? [];
  const selectedSlugs = accountSlugs ?? accounts.map((a) => a.slug);

  const rollup = (item.context?.aux_sync_apply ?? null) as Record<string, number> | null;

  const start = async () => {
    setLaunching(true);
    try {
      await launch({
        integration_name: integration,
        ...(accountSlugs && accountSlugs.length && accountSlugs.length < accounts.length
          ? { accounts: accountSlugs }
          : {}),
        ...(fromDate ? { from_date: format(fromDate, 'yyyy-MM-dd') } : {}),
        ...(toDate ? { to_date: format(toDate, 'yyyy-MM-dd') } : {}),
      });
      snackbar.show(
        'Aux info sync started — it pauses for review once the records are extracted.',
        'success',
      );
    } catch (e) {
      snackbar.show((e as Error).message, 'error');
    } finally {
      setLaunching(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
          Extracts the auxiliary data ops otherwise collect by hand — package / question / resource
          / custom-field IDs, guide &amp; vehicle rosters, tag-mapping candidates — from the booking
          system&apos;s APIs plus a sample of real bookings. Nothing is written until the records
          are reviewed and applied; re-running later is always safe.
        </Typography>

        {item.status !== 'completed' && item.blocked_by.length > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <BlockedNotice blockers={item.blocked_by} />
          </Alert>
        )}
        {item.status === 'error' && item.error_message && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {item.error_message}
          </Alert>
        )}
        {item.status === 'completed' && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Auxiliary info applied
            {rollup
              ? ` — ${['created', 'updated', 'unchanged']
                  .filter((k) => rollup[k])
                  .map((k) => `${rollup[k]} ${k}`)
                  .join(', ')}`
              : ''}
            .
          </Alert>
        )}

        {taskId && (
          <Box sx={{ mb: 2 }}>
            <TaskProgressCard taskId={taskId} onTerminal={() => actions.invalidate()} />
            <Button
              sx={{ mt: 1 }}
              variant={running ? 'contained' : 'outlined'}
              size="small"
              component={RouterLink}
              to={`/sync/aux-info/${taskId}/review`}
              startIcon={<RateReviewIcon />}
            >
              {running ? 'Review extracted records' : 'View extracted records'}
            </Button>
          </Box>
        )}

        {canManage ? (
          options.length > 0 ? (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                select
                size="small"
                label="Integration"
                value={integration ?? ''}
                onChange={(e) => {
                  setIntegration(e.target.value || null);
                  setAccountSlugs(null);
                }}
                sx={{ minWidth: 180 }}
                disabled={options.length === 1}
              >
                {options.map((o) => (
                  <MenuItem key={o.integration} value={o.integration}>
                    {o.integration}
                  </MenuItem>
                ))}
              </TextField>
              {/* Multi-account restechs only: empty selection = every account. */}
              {accounts.length > 1 && (
                <TextField
                  select
                  size="small"
                  label="Accounts"
                  value={selectedSlugs.map((s) => s ?? '')}
                  onChange={(e) => {
                    const raw = e.target.value as unknown as string[];
                    setAccountSlugs(raw.map((s) => (s === '' ? null : s)));
                  }}
                  sx={{ minWidth: 220 }}
                  helperText={
                    accountSlugs && accountSlugs.length === 0
                      ? 'Pick at least one account'
                      : `${selectedSlugs.length} of ${accounts.length} accounts`
                  }
                  error={accountSlugs?.length === 0}
                  SelectProps={{
                    multiple: true,
                    renderValue: (v) =>
                      accounts
                        .filter((a) => (v as string[]).includes(a.slug ?? ''))
                        .map((a) => a.display_name)
                        .join(', '),
                  }}
                >
                  {accounts.map((a) => (
                    <MenuItem key={a.slug ?? '_default'} value={a.slug ?? ''}>
                      <Checkbox
                        size="small"
                        checked={selectedSlugs.some((s) => (s ?? '') === (a.slug ?? ''))}
                      />
                      <ListItemText primary={a.display_name} />
                    </MenuItem>
                  ))}
                </TextField>
              )}
              <DatePicker
                label="From"
                value={fromDate}
                onChange={setFromDate}
                slotProps={{
                  field: { clearable: true },
                  textField: {
                    size: 'small',
                    sx: { width: 170 },
                    helperText: !fromDate && !toDate ? 'default: last 7 days' : undefined,
                  },
                }}
              />
              <DatePicker
                label="To"
                value={toDate}
                minDate={fromDate ?? undefined}
                onChange={setToDate}
                slotProps={{
                  field: { clearable: true },
                  textField: { size: 'small', sx: { width: 170 } },
                }}
              />
              <Button
                variant="contained"
                size="small"
                disabled={!integration || accountSlugs?.length === 0 || launching || running}
                onClick={start}
              >
                {item.status === 'completed' ? 'Run again' : 'Start sync'}
              </Button>
              <Tooltip title="View item log">
                <IconButton
                  size="small"
                  onClick={() => setLogOpen(true)}
                  aria-label="View item log"
                >
                  <HistoryIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ) : (
            <Alert severity="info">No configured booking integration on this operator.</Alert>
          )
        ) : (
          <Alert severity="info">
            Read-only access — launching needs the preflight manage permission.
          </Alert>
        )}

        <PreflightItemRunLogDrawer
          topId={topId}
          itemId={logOpen ? item.id : null}
          title={item.title}
          open={logOpen}
          onClose={() => setLogOpen(false)}
        />
      </Box>
    </LocalizationProvider>
  );
}
